import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Loader2, X, AlertTriangle } from "lucide-react";
import { authClient } from "@/context/CustomerAuthContext";
import { fileUrl } from "@/pages/ReceiptPage";

export default function ClaimDialog({ booking, open, onClose, onSubmitted, smsConsent }) {
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [pref, setPref] = useState(smsConsent ? "text" : "email");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files || [])) {
        if (!/^image\//.test(f.type)) continue;
        const fd = new FormData(); fd.append("file", f);
        const { data } = await authClient.post("/account/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.url]);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo upload failed"); } finally { setUploading(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await authClient.post("/account/claims", { booking_id: booking.id, description, photo_urls: photos, contact_preference: pref });
      toast.success("Claim sent — we'll review your photos and get back to you within 2 business days", { duration: 7000 });
      onSubmitted?.(data); onClose();
      setDescription(""); setPhotos([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send your claim"); } finally { setSaving(false); }
  };

  const late = booking?.claim_window && !booking.claim_window.open;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-auto" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-[#0C2E2B]">Report damage · {booking?.ref_id}</DialogTitle>
          <DialogDescription>Tell us what happened and add photos. We repair, replace or reimburse per our <a href="/terms#delivery" target="_blank" rel="noreferrer" className="underline">delivery terms</a>.</DialogDescription>
        </DialogHeader>
        {late && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border-l-2 border-amber-500 px-3 py-2 rounded-r" data-testid="claim-late-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" /> The 48-hour window has passed. You can still send this — we'll review it, but coverage isn't guaranteed.
          </div>
        )}
        <div className="space-y-4">
          <Textarea data-testid="claim-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="What was damaged, where on the item, and when you noticed it" className="rounded-md" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Photos of the damage (required)</div>
            <div className="flex flex-wrap gap-2">
              {photos.map((u, i) => (
                <div key={u} className="relative w-20 h-20 rounded-md overflow-hidden border-2 border-zinc-200" data-testid={`claim-photo-${i}`}>
                  <img src={fileUrl(u)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" data-testid="claim-add-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-20 rounded-md border-2 border-dashed border-zinc-300 hover:border-[#0C2E2B] grid place-items-center text-zinc-500">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              </button>
              <input ref={fileRef} data-testid="claim-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">How should we reach you?</div>
            <div className="flex gap-2">
              {[["email", "Email"], ["text", "Text message"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`claim-pref-${v}`} onClick={() => setPref(v)} disabled={v === "text" && !smsConsent} className={`px-3 h-9 rounded-md border-2 text-xs font-bold ${pref === v ? "border-[#FFDF00] bg-[#FFDF00]/20 text-[#0C2E2B]" : "border-zinc-200 text-zinc-600"} disabled:opacity-40`}>{l}</button>
              ))}
            </div>
            {!smsConsent && <p className="text-[10px] text-zinc-500 mt-1">Text is available when you've opted in to texts on a booking.</p>}
          </div>
          <Button data-testid="claim-submit-btn" onClick={submit} disabled={saving || uploading || description.trim().length < 10 || photos.length === 0} className="w-full h-11 rounded-md bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 font-black">
            {saving ? "Sending…" : "Send claim"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
